// File: src/pages/Business.jsx
// BUCKET: Customer Intelligence
// "Who buys, why they buy, what makes them stick"

import React, { useState, useContext, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Eye,
  ArrowRight,
  ArrowLeft,
  Users,
  ShoppingCart,
  Percent
} from 'lucide-react';
import { Tooltip } from '@shopify/polaris';
import LayoutWrapper from '../components/Layout/Layout';
import { AuthContext } from '../context/AuthContext';
import './DashboardBucket.css';
import useData from '../hooks/useData';
import DateRangePills from '../components/ui/DateRangePills';

// Skeleton loader for 2 KPI cards
const KPISkeleton = () => (
  <>
    <div className="stat-item stat-item--centered skeleton">
      <div className="skeleton-icon" style={{ width: '20px', height: '20px', background: '#e0e0e0', borderRadius: '4px' }}></div>
      <div className="stat-content">
        <div className="skeleton-value" style={{ width: '60px', height: '20px', background: '#e0e0e0', borderRadius: '4px', marginBottom: '4px' }}></div>
        <div className="skeleton-label" style={{ width: '80px', height: '14px', background: '#f0f0f0', borderRadius: '4px' }}></div>
      </div>
    </div>
    <div className="stat-item stat-item--centered skeleton">
      <div className="skeleton-icon" style={{ width: '20px', height: '20px', background: '#e0e0e0', borderRadius: '4px' }}></div>
      <div className="stat-content">
        <div className="skeleton-value" style={{ width: '50px', height: '20px', background: '#e0e0e0', borderRadius: '4px', marginBottom: '4px' }}></div>
        <div className="skeleton-label" style={{ width: '70px', height: '14px', background: '#f0f0f0', borderRadius: '4px' }}></div>
      </div>
    </div>
  </>
);

export default function Business() {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL || 'https://api.baobrain.com';

  const siteId = sessionStorage.getItem('site_id') || null;

  const [daysRange, setDaysRange] = useState(sessionStorage.getItem('days_range') || '7');

  const handleRangeChange = useCallback((value) => {
    setDaysRange(value);
    sessionStorage.setItem('days_range', value);
  }, []);

  const { data: overviewStats, loading: statsLoading, error: statsError } = useData(
    siteId ? `overview:${siteId}:${daysRange}` : 'noop',
    () => fetch(`${API_URL}/api/business/overview?site_id=${siteId}&days=${daysRange}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.json()),
    { ttlMs: 24 * 60 * 60 * 1000, deps: [siteId, daysRange, token] }
  );

  const { data: sites = [], loading: sitesLoading, error: sitesError } = useData(
    `sites:${token}`,
    () => fetch(`${API_URL}/api/sites`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
    { ttlMs: 24 * 60 * 60 * 1000, deps: [token] }
  );

  const { data: sessionsData } = useData(
    siteId ? `businessSessions:${siteId}:${daysRange}` : 'noop',
    () => fetch(`${API_URL}/api/business/sessions?site_id=${siteId}&days=${daysRange}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.json()),
    { ttlMs: 24 * 60 * 60 * 1000, deps: [siteId, daysRange, token] }
  );

  // Calculate conversion rate
  const visitors = sessionsData?.sessions || 0;
  const orders = overviewStats?.orders_7d || 0;
  const calculatedConversionRate = visitors > 0 && orders > 0 
    ? ((orders / visitors) * 100).toFixed(2)
    : 0;

  // 2 widget cards for Customer Intelligence bucket
  const widgets = useMemo(() => ([
    {
      title: 'Customer Dashboard',
      description: 'Segments, LTV, retention, repeat rates & behavioral patterns',
      detailsPath: '/customerdashboard',
      icon: Eye,
      bgGradient: 'linear-gradient(135deg, #f59e0b, #d97706)',
    },
    {
      title: 'Visitor Journeys',
      description: 'Session replay & individual journey analysis',
      detailsPath: '/sessions',
      icon: Users,
      bgGradient: 'linear-gradient(135deg, #10b981, #059669)',
    },
  ]), []);

  if (!token) {
    return (
      <LayoutWrapper>
        <div className="bucket-page">
          <div className="bucket-container">
            <div className="auth-required">
              <div className="auth-icon">
                <Users size={48} />
              </div>
              <h2>Authentication Required</h2>
              <p>Please log in to access customer intelligence insights.</p>
              <button className="auth-btn" onClick={() => navigate('/login')}>
                Sign In
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </LayoutWrapper>
    );
  }

  const isLoading = statsLoading || sitesLoading;
  const hasError = statsError || sitesError;
  const noSites = !sites.length;

  return (
    <LayoutWrapper>
      <div className="bucket-page">
        <div className="bucket-container">
          <div className="back-button-wrapper">
            <button className="back-icon-button" onClick={() => navigate('/Dashboard')}>
              <ArrowLeft size={20} />
            </button>
          </div>
          
          <div className="bucket-header">
            <div className="header-content">
              <div className="header-badge">
                <Users size={16} />
                <span>Customer Intelligence</span>
              </div>
              <h1>Customer Intelligence</h1>
              <p>Who buys, why they buy, and what makes them stick. Understand your customers deeply.</p>
            </div>
            
            <div className="date-range-wrapper">
              <DateRangePills value={daysRange} onChange={handleRangeChange} />
            </div>
          </div>
          
          {/* 2 KPI Cards - Centered */}
          <div className="quick-stats quick-stats--center-2">
            {isLoading ? (
              <KPISkeleton />
            ) : hasError || noSites ? (
              <div className="stats-error-indicator">
                <p>Error: {hasError ? (statsError || sitesError) : "No sites connected."}</p>
                {noSites && (
                  <button className="auth-btn" onClick={() => navigate('/sites')}>Connect a Site</button>
                )}
              </div>
            ) : (
              <>
                <div className="stat-item stat-item--centered">
                  <Percent className="stat-icon" size={20} />
                  <div className="stat-content">
                    <span className="stat-value">
                      {overviewStats?.revenue_growth_pct !== undefined
                        ? `${overviewStats.revenue_growth_pct > 0 ? '+' : ''}${Number(overviewStats.revenue_growth_pct).toFixed(2)}%`
                        : 'N/A'}
                    </span>
                    <span className="stat-label">Revenue Growth</span>
                  </div>
                </div>
                <div className="stat-item stat-item--centered">
                  <ShoppingCart className="stat-icon" size={20} />
                  <div className="stat-content">
                    <span className="stat-value">{calculatedConversionRate}%</span>
                    <Tooltip content="Share of visits that resulted in an order." preferredPosition="above">
                      <span className="stat-label" style={{ textDecoration: 'underline dotted', textUnderlineOffset: '3px', cursor: 'help' }}>
                        Conversion Rate
                      </span>
                    </Tooltip>
                  </div>
                </div>
              </>
            )}
          </div>

          <p className="focus-line">Focus: Understanding who your customers are.</p>
          
          {/* Widget Cards */}
          <div className="widgets-grid widgets-grid--center-2">
            {widgets.map(({ title, description, detailsPath, icon: Icon, bgGradient }) => (
              <div key={title} className="widget-card">
                <div className="widget-header">
                  <div className="widget-icon" style={{ background: bgGradient }}>
                    <Icon size={24} color="white" />
                  </div>
                </div>
                <div className="widget-content">
                  <h3>{title}</h3>
                  <p className="widget-description">{description}</p>
                </div>
                <div className="widget-footer">
                  <button className="widget-btn" onClick={() => navigate(detailsPath)}>
                    Analyze Now
                    <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div> 
      </div>
    </LayoutWrapper>
  );
}